import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import crochet1 from '../images/crochet1.jpg';
import crochet2 from '../images/crochet2.jpg';
import crochet3 from '../images/crochet3.jpg';
import logo from '../images/landp.png';
import { FaHeart, FaEtsy, FaInstagram, FaFacebook, FaStar } from 'react-icons/fa';
import { MdMenu, MdClose } from 'react-icons/md';
import Carousel from '../components/Carousel';

// Animation keyframes
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const sparkle = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
`;

//  Reviews marquee (never-ending loop)
const marquee = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

// Navbar Component
const Navbar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(5px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 100;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 0.8rem 1rem;
  }
`;

const NavLogo = styled.img`
  height: 50px;
  width: auto;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    position: fixed;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2rem;
    top: 0;
    right: ${props => (props.isOpen ? '0' : '-100%')};
    width: 70%;
    height: 100vh;
    background: linear-gradient(135deg, #FFCDE7 0%, #F3E3F7 100%);
    transition: right 0.3s ease-in-out;
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
    padding: 4rem 2rem;
  }
`;

const NavLink = styled.a`
  color: #7A4988;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 500;
  position: relative;
  transition: all 0.2s ease;

  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -4px;
    left: 0;
    background-color: #FF85C0;
    transition: width 0.3s ease;
  }

  &:hover {
    color: #FF85C0;
    transform: translateY(-2px);

    &:after {
      width: 100%;
    }
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.8rem;
  color: #7A4988;
  cursor: pointer;
  z-index: 150;

  @media (max-width: 768px) {
    display: block;
  }
`;

// Hero Section
const HeroSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6rem 2rem 4rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #FFCDE7 0%, #F3E3F7 40%, #FFF5FC 100%);
  position: relative;
  overflow: hidden;
  text-align: center;

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(circle at 20% 30%, rgba(255, 170, 215, 0.3) 0%, transparent 20%),
      radial-gradient(circle at 80% 40%, rgba(182, 126, 235, 0.3) 0%, transparent 25%),
      radial-gradient(circle at 40% 80%, rgba(255, 170, 215, 0.2) 0%, transparent 15%);
  }
`;

const FloatingHearts = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
`;

const Heart = styled.div`
  position: absolute;
  color: rgba(255, 170, 215, 0.3);
  font-size: ${props => props.size || '1.5rem'};
  animation: ${float} ${props => props.duration || '6s'} ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
  top: ${props => props.top || '20%'};
  left: ${props => props.left || '50%'};
`;

const Logo = styled.img`
  width: min(500px, 90%);
  height: auto;
  margin-bottom: 2rem;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
  transition: all 0.5s ease;
  animation: ${float} 6s ease-in-out infinite;
  position: relative;
  z-index: 2;

  &:hover {
    transform: scale(1.08) rotate(3deg);
    filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.2));
  }
`;

const Headline = styled.h1`
  color: #7A4988;
  font-size: clamp(2.5rem, 5vw, 3.8rem);
  margin-bottom: 0.5rem;
  letter-spacing: 1.5px;
  font-family: 'Dancing Script', cursive;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;

  &::after {
    content: "♡";
    position: absolute;
    font-size: 0.8em;
    top: -0.5rem;
    right: -1.5rem;
    color: #FF85C0;
    animation: ${sparkle} 2s ease-in-out infinite;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;

    &::after {
      right: -1rem;
      top: -0.3rem;
    }
  }
`;

const Tagline = styled.p`
  color: #5a5a5a;
  font-size: clamp(1.1rem, 2.5vw, 1.4rem);
  max-width: 700px;
  line-height: 1.6;
  margin: 0 auto 2.5rem auto;
  position: relative;
  z-index: 2;
`;

// About Section
const AboutSection = styled.section`
  background-color: #fff;
  padding: 3.5rem 2rem;
  max-width: 1200px;
  margin: -80px auto 3rem;
  border-radius: 1.5rem;
  box-shadow: 0 8px 30px rgba(122, 73, 136, 0.15);
  position: relative;
  z-index: 10;

  &::before {
    content: "";
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 95%;
    height: 20px;
    background-color: rgba(255, 255, 255, 0.7);
    border-radius: 10px 10px 0 0;
    z-index: -1;
  }

  @media (max-width: 768px) {
    margin-top: -40px;
    padding: 2.5rem 1.5rem;
  }
`;

const AboutTitle = styled.h2`
  color: #7A4988;
  font-size: 2.4rem;
  font-family: 'Poppins', sans-serif;
  text-align: center;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(to right, #FFAAD7, #B67EEB);
    border-radius: 3px;
  }
`;

const AboutContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const AboutContent = styled.p`
  color: #555;
  line-height: 1.8;
  text-align: center;
  margin: 1.5rem auto;
  font-size: 1.1rem;
  position: relative;
  padding: 0 1rem;

  &:first-of-type::before {
    content: "“";
    position: absolute;
    font-size: 4rem;
    color: rgba(122, 73, 136, 0.1);
    font-family: serif;
    top: -20px;
    left: -10px;
  }

  &:last-of-type::after {
    content: "”";
    position: absolute;
    font-size: 4rem;
    color: rgba(122, 73, 136, 0.1);
    font-family: serif;
    bottom: -50px;
    right: -10px;
  }
`;

// Carousel Wrapper
const CarouselWrapper = styled.section`
  margin: 7rem 0;
`;

//  Reviews Section endless loop
const ReviewsSection = styled.section`
  max-width: 1200px;
  margin: 0 auto 4rem;
  padding: 0 2rem;
`;

const ReviewsTitle = styled.h2`
  color: #7A4988;
  font-size: 2.2rem;
  font-family: 'Poppins', sans-serif;
  text-align: center;
  margin: 1rem 0 1.25rem;
`;

const ReviewsSub = styled.p`
  text-align: center;
  color: #666;
  margin: 0 auto 1.5rem;
  max-width: 760px;
  line-height: 1.6;
`;

const ReviewsMarquee = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(255, 205, 231, 0.55), rgba(243, 227, 247, 0.65));
  box-shadow: 0 10px 30px rgba(122, 73, 136, 0.12);
  padding: 1.25rem 0;

  /* soft fade edges */
  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    width: 90px;
    height: 100%;
    z-index: 2;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, rgba(245, 232, 246, 0.95), rgba(245, 232, 246, 0));
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, rgba(245, 232, 246, 0.95), rgba(245, 232, 246, 0));
  }

  @media (prefers-reduced-motion: reduce) {
    padding: 1rem;
  }
`;

const ReviewsTrack = styled.div`
  display: flex;
  gap: 1.25rem;
  width: max-content;
  animation: ${marquee} 60s linear infinite;

  ${ReviewsMarquee}:hover & {
    animation-play-state: paused; /* pause on hover */
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
  }
`;

const ReviewCard = styled.div`
  flex: 0 0 auto;
  width: 360px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 16px;
  padding: 1rem 1.1rem;
  box-shadow: 0 8px 20px rgba(122, 73, 136, 0.12);
  border: 1px solid rgba(122, 73, 136, 0.08);

  @media (max-width: 420px) {
    width: 300px;
  }
`;

const ReviewTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const ReviewName = styled.div`
  color: #7A4988;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
`;

const ReviewDate = styled.div`
  color: #7a7a7a;
  font-size: 0.9rem;
  font-family: 'Poppins', sans-serif;
`;

const Stars = styled.div`
  display: flex;
  gap: 0.25rem;
  color: #FF85C0;
  margin: 0.2rem 0 0.7rem;
`;

const ReviewText = styled.p`
  margin: 0;
  color: #444;
  line-height: 1.55;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
`;

const Footer = styled.footer`
  padding: 3rem 2rem 1.5rem;
  background-color: #7A4988;
  color: #fff;
  font-family: 'Poppins', sans-serif;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1.2rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 30px;
    height: 2px;
    background-color: #FFAAD7;
  }
`;

const FooterLink = styled.a`
  color: #F3E3F7;
  text-decoration: none;
  margin-bottom: 0.8rem;
  transition: all 0.3s ease;

  &:hover {
    color: #FFAAD7;
    transform: translateX(3px);
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const SocialIcon = styled.a`
  color: #F3E3F7;
  font-size: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    color: #FFAAD7;
    transform: translateY(-3px);
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.9rem;
`;

function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scrolling effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const reviews = [
    {
      name: 'Kathleen',
      date: 'Aug 4, 2025',
      text:
        'The seller was quick to respond when I reached out with a question. The item is well made & so adorable, I cannot wait to gift this!',
    },
    {
      name: 'LavenderandPalm Customer',
      date: 'Nov 18, 2025',
      text:
        'I cannot recommend this shop highly enough! LavenderandPalm was incredibly kind and easy to work with. The original listing was for a dog, but she happily customized it into a cat for me, and it turned out absolutely adorable.',
    },
    {
      name: 'kermi75',
      date: 'Oct 30, 2025',
      text:
        'Wonderful job. My son was very excited when it arrived. Soft and squishy and matches with our late basset hound’s pictures.',
    },
    {
      name: 'Niki',
      date: 'Oct 28, 2025',
      text:
        'These are such special, beautiful gifts for my kiddos after losing their beloved dog. Thank you!',
    },
    {
      name: 'Shellyann',
      date: 'Oct 15, 2025',
      text:
        'This duck exceeded my expectations such great workmanship and soft and cuddly. Thank you for making her for me she’s stunning. The item matched the description to the tee.',
    },
    {
      name: 'Chasity',
      date: 'Oct 3, 2025',
      text: 'Adorable item. Very soft & great size.',
    },
    {
      name: 'Gena',
      date: 'Sep 15, 2025',
      text:
        'OMG it is so cute she did an AMAZING match to my dog SO CUTE 🥰',
    },
    {
      name: 'Kristen',
      date: 'Aug 27, 2025',
      text:
        'I love my Buddy. He turned out amazing! I am so glad I ordered him. I love his color, and all of his details. I will definitely order from this seller again. 🩷 Thank you so so much!',
    },
    {
      name: 'shanda',
      date: 'Apr 4, 2025',
      text: 'These are wonderful to give as gifts. I adore them.',
    },
    {
      name: 'MaryAnn',
      date: 'Mar 9, 2025',
      text:
        'Such a precious gift for my nephew! Wonderfully made and fast shipping. Thank you for the beautiful work ❤️',
    },
    {
      name: 'marybozzacco',
      date: 'Feb 8, 2025',
      text:
        'What a darling handmade gift! The item is just as it is described and pictured in the listing. The doll is soft and oh so cute. The shipping took a while, but it is what I expected with the initial delivery date estimate. When I reached out to the seller with a question, she responded quickly. Happy with my experience!',
    },
    {
      name: 'amix2',
      date: 'Jan 18, 2025',
      text:
        'Super cute and just as pictured. Perfect for our little one coming soon.',
    },
  ];

  // duplicate array for seamless loop (track moves -50%)
  const reviewsLoop = [...reviews, ...reviews];

  return (
    <>
      {/* Navbar */}
      <Navbar
        style={{
          boxShadow: scrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : 'none',
        }}
      >
        <NavLogo src={logo} alt="Lavender & Palm Co." />

        <MenuButton onClick={toggleMenu}>
          {menuOpen ? <MdClose /> : <MdMenu />}
        </MenuButton>

        <NavLinks isOpen={menuOpen}>
          <NavLink href="/">Home</NavLink>
          <NavLink href="/custom-order">Custom Orders</NavLink>
          <NavLink href="/gallery">Gallery</NavLink>
          <NavLink href="/contact">Contact</NavLink>
        </NavLinks>
      </Navbar>

      {/* Hero Section */}
      <HeroSection>
        <FloatingHearts>
          <Heart top="10%" left="10%" size="1.2rem" duration="7s" delay="0.5s">
            <FaHeart />
          </Heart>
          <Heart top="20%" left="85%" size="1.8rem" duration="8s" delay="1s">
            <FaHeart />
          </Heart>
          <Heart top="70%" left="15%" size="1.5rem" duration="6s" delay="1.5s">
            <FaHeart />
          </Heart>
          <Heart top="40%" left="90%" size="1.3rem" duration="9s" delay="2s">
            <FaHeart />
          </Heart>
          <Heart top="85%" left="75%" size="1.7rem" duration="7s" delay="0s">
            <FaHeart />
          </Heart>
          <Heart top="60%" left="5%" size="1.4rem" duration="8s" delay="2.5s">
            <FaHeart />
          </Heart>
        </FloatingHearts>

        <Logo src={logo} alt="Lavender & Palm Co. Logo" />
        <Headline>Lavender & Palm Co.</Headline>
        <Tagline>Handmade crochet creations infused with love</Tagline>
      </HeroSection>

      <CarouselWrapper>
        <Carousel />
      </CarouselWrapper>

      {/* About Section */}
      <AboutSection>
        <AboutTitle>About</AboutTitle>
        <AboutContentWrapper>
          <AboutContent>
            Hi friend, and welcome to Lavender & Palm Co.! We’re so happy you
            stopped by — it truly means the world to us. Here, you’ll find
            heirloom-quality handmade crochet treasures, crafted with care and a
            whole lot of love. Whether you’re searching for the perfect baby
            shower gift or something extra special for a birthday, we’d be
            absolutely delighted to help you find just what your heart is
            looking for.
          </AboutContent>
          <AboutContent>
            Each creation is carefully made with premium yarns for that
            wonderfully soft and snuggly feel. Our name comes from our love of a
            relaxed home atmosphere where love and comfort are celebrated. We
            hope our crochet treasures bring a little joy to your day and a
            touch of warmth to your home!
          </AboutContent>
        </AboutContentWrapper>
      </AboutSection>

      {/* Etsy Reviews Loop never ending */}
      <ReviewsSection>
        <ReviewsTitle>Our Reviews From Etsy</ReviewsTitle>
        <ReviewsSub>
          Thank you to our customers for supporting handmade. 🩷
          (Hover to pause)
        </ReviewsSub>

        <ReviewsMarquee>
          <ReviewsTrack>
            {reviewsLoop.map((r, idx) => (
              <ReviewCard key={`${r.name}-${r.date}-${idx}`}>
                <ReviewTop>
                  <ReviewName>{r.name}</ReviewName>
                  <ReviewDate>{r.date}</ReviewDate>
                </ReviewTop>

                <Stars aria-label="5 out of 5 stars">
                  <FaStar />
                  <FaStar />
                  <FaStar />
                  <FaStar />
                  <FaStar />
                </Stars>

                <ReviewText>{r.text}</ReviewText>
              </ReviewCard>
            ))}
          </ReviewsTrack>
        </ReviewsMarquee>
      </ReviewsSection>

      {/* Footer */}
      <Footer>
        <FooterContent>
          <FooterSection>
            <FooterTitle>Quick Links</FooterTitle>
            <FooterLink href="/">Home</FooterLink>
            <FooterLink href="/custom-order">Custom Orders</FooterLink>
            <FooterLink href="/faq">FAQ</FooterLink>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Policies</FooterTitle>
            <FooterLink href="/shipping">Shipping</FooterLink>
            <FooterLink href="/returns">Returns</FooterLink>
            <FooterLink href="/privacy">Privacy Policy</FooterLink>
            <FooterLink href="/terms">Terms of Service</FooterLink>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Connect With Us</FooterTitle>
            <p>Follow us for updates and behind-the-scenes content!</p>
            <SocialLinks>
              <SocialIcon href="https://www.instagram.com/lavenderandpalmcrochet/">
                <FaInstagram />
              </SocialIcon>
              <SocialIcon href="https://www.facebook.com/people/Lavender-and-Palm-Crochet/61566023416208/#">
                <FaFacebook />
              </SocialIcon>
              <SocialIcon href="https://lavenderandpalm.etsy.com">
                <FaEtsy />
              </SocialIcon>
            </SocialLinks>
          </FooterSection>
        </FooterContent>

        <Copyright>
          © {new Date().getFullYear()} Lavender & Palm Co. All rights reserved.
        </Copyright>
      </Footer>
    </>
  );
}

export default Home;